'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from '@/i18n/routing';
import { api, ApiError } from '@/lib/api';

type Status = 'loading' | 'authed' | 'unauth';

// AppGate requires login for every page — unauthenticated users go to /login.
// Beyond that, access is freemium: every logged-in member (free / Pro / Pro Max)
// can use the app, limited by per-plan quotas (AI/day, columns/month) enforced
// by the backend. No full-content mosaic.
export default function AppGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [status, setStatus] = useState<Status>('loading');

  // Public pages need no session: the login screen and the password-reset link.
  const isLogin = pathname === '/login' || pathname === '/reset';

  // While on /login the previous 'unauth' verdict is stale — the user is about
  // to sign in. Reset to 'loading' so leaving /login re-checks instead of the
  // redirect effect instantly bouncing them back (login → /market → /login loop).
  useEffect(() => {
    if (isLogin) setStatus('loading');
  }, [isLogin]);

  useEffect(() => {
    if (isLogin) return;
    let active = true;
    api
      .usage()
      .then(() => active && setStatus('authed'))
      .catch((e) => {
        if (!active) return;
        // 401 → not logged in. Other errors (network) shouldn't lock the user out.
        setStatus(e instanceof ApiError && e.status === 401 ? 'unauth' : 'authed');
      });
    // Admin/客服 belong in the dashboard (which has logout); the consumer
    // Header + BottomNav are hidden for them, so a consumer page would trap
    // them with no chrome. Send them to /admin with a HARD navigation — a soft
    // router.replace here races Next's router hydration (esp. after the "/" →
    // /market server redirect) and throws React #310, crashing the whole app.
    if (!pathname.startsWith('/admin')) {
      api
        .me()
        .then((u) => {
          if (active && u.role === 'admin') window.location.replace('/admin');
        })
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [pathname, isLogin, router]);

  useEffect(() => {
    if (status === 'unauth' && !isLogin) router.replace('/login');
  }, [status, isLogin, router]);

  if (isLogin) return <>{children}</>;
  if (status !== 'authed') return <Splash />;
  return <>{children}</>;
}

function Splash() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center text-sm text-zinc-400">載入中…</div>
  );
}
